"use client";

import { useState, useEffect } from "react";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";

export function LoginForm({ shopName: envShopName }: { shopName: string }) {
  const router = useRouter();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [shopName, setShopName] = useState(envShopName);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [offNetwork, setOffNetwork] = useState(false);

  useEffect(() => {
    fetch("/api/settings/public")
      .then((r) => r.json())
      .then((d) => {
        if (d.shopName) setShopName(d.shopName);
        if (d.logoDataUrl) setLogoDataUrl(d.logoDataUrl);
      })
      .catch(() => {});

    // Check whether this device is on the shop network
    fetch("/api/shop-check")
      .then((r) => r.json())
      .then((d) => { if (d.enabled && !d.allowed) setOffNetwork(true); })
      .catch(() => {});
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (offNetwork) {
      setError("You must be connected to the shop network to sign in.");
      return;
    }
    setLoading(true);
    const res = await signIn("credentials", { username, password, redirect: false });
    setLoading(false);
    if (res?.error) {
      setError(offNetwork
        ? "You must be connected to the shop network to sign in."
        : "Invalid username or password.");
    } else {
      router.push("/");
    }
  }

  return (
    <main
      className="login-splash min-h-screen flex flex-col items-center justify-center p-6"
      style={{ background: "#1c150e" }}
    >
      <div className="w-full max-w-xs">

        {/* Shop identity */}
        <div className="mb-10">
          {logoDataUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={logoDataUrl} alt={shopName} className="h-20 w-auto object-contain mb-5" />
          ) : (
            <span className="text-[var(--brand)] block mb-4" style={{ fontSize: 22, lineHeight: 1 }}>■</span>
          )}
          <h1
            className="text-white leading-none mb-3"
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 700,
              fontSize: "clamp(2.4rem, 8vw, 3.2rem)",
              letterSpacing: "0.03em",
              textTransform: "uppercase",
            }}
          >
            {shopName}
          </h1>
          <p
            className="text-white/30"
            style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.18em" }}
          >
            DIGITAL VEHICLE INSPECTION
          </p>
        </div>

        {/* Divider */}
        <div className="mb-8" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }} />

        {/* Off-network notice */}
        {offNetwork && (
          <div
            className="mb-5 rounded-lg px-4 py-3"
            style={{ background: "rgba(232,66,26,0.10)", border: "1px solid rgba(232,66,26,0.35)" }}
          >
            <p className="text-[#ff7a52]" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, lineHeight: 1.5 }}>
              Sign-in is restricted to the shop network. Connect to the shop&apos;s WiFi to continue.
            </p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              className="block mb-2 text-white/40"
              htmlFor="username"
              style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase" }}
            >
              Username
            </label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded-lg px-4 py-3 text-white text-base focus:outline-none transition-colors"
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.10)",
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = "var(--brand)")}
              onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)")}
            />
          </div>

          <div>
            <label
              className="block mb-2 text-white/40"
              htmlFor="password"
              style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase" }}
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg px-4 py-3 text-white text-base focus:outline-none transition-colors"
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.10)",
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = "var(--brand)")}
              onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)")}
            />
          </div>

          {error && (
            <p
              className="text-[var(--brand)]"
              style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || offNetwork}
            className="w-full text-white rounded-lg py-3.5 transition-colors disabled:opacity-40 mt-1"
            style={{
              background: "var(--brand)",
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 700,
              fontSize: 15,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}
            onMouseEnter={(e) => !loading && (e.currentTarget.style.background = "var(--brand-strong)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "var(--brand)")}
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </main>
  );
}
